import { type RefObject, useEffect, useRef, useState } from 'react';

/**
 * Check if a pointer is pressed on an element.
 */
export function useIsPointerPressed(
    ref: RefObject<Element>,
    options?: {
        /**
         * Delay the pointer up.
         * Can be use to avoid blinking when double-click.
         */
        delayPointerUp?: boolean;
    }
) {
    const delayPointerUp = options?.delayPointerUp ?? false;
    const [isPressed, setIsPressed] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }

        const handlePointerDown = (event: Event) => {
            if (event instanceof PointerEvent && event.button === 0) {
                if (delayPointerUp && timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                setIsPressed(true);
            }
        };

        const handlePointerUp = (event: Event) => {
            if (event instanceof PointerEvent && event.button === 0) {
                if (delayPointerUp) {
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                    timeoutRef.current = setTimeout(() => {
                        setIsPressed(false);
                    }, 150);
                } else {
                    setIsPressed(false);
                }
            }
        };

        const handlePointerCancel = () => {
            setIsPressed(false);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };

        element.addEventListener('pointerdown', handlePointerDown, { capture: true });

        // The up can happen actually anywhere, we don't care if it's on the element itself
        // that's why we are using document here.
        document.addEventListener('pointerup', handlePointerUp, { capture: true });
        document.addEventListener('pointercancel', handlePointerCancel, { capture: true });

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            element.removeEventListener('pointerdown', handlePointerDown, { capture: true });
            document.removeEventListener('pointerup', handlePointerUp, { capture: true });
            document.removeEventListener('pointercancel', handlePointerCancel, { capture: true });
        };
    }, [delayPointerUp, ref]);
    return isPressed;
}
